import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, Scale, FolderOpen, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase/client";
import { AuthError, AuthApiError } from "@supabase/supabase-js";
import { resendConfirmationEmail } from "@/lib/services/auth";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import Logo from "@/components/Logo";
import Turnstile from "@/components/common/Turnstile";
import { TermsModal } from "@/components/TermsModal";



const formSchema = z.object({
  email: z
    .string()
    .min(1, "El correo electrónico es requerido")
    .email("Debe ser un correo electrónico válido"),
  password: z
    .string()
    .min(1, "La contraseña es requerida")
    .min(8, "La contraseña debe tener al menos 8 caracteres"),
});

type FormData = z.infer<typeof formSchema>;

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn } = useAuth();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Clear field errors when user starts typing
  const clearFieldError = (fieldName: keyof FormData) => {
    if (form.formState.errors[fieldName]) {
      form.clearErrors(fieldName);
    }
  };

  const handleResendConfirmationEmail = async () => {
    const email = form.getValues("email");
    if (!email) {
      toast({
        title: "Error",
        description: "Por favor ingresa tu correo electrónico primero",
        variant: "destructive",
      });
      return;
    }

    setResendingEmail(true);
    try {
      const { error } = await resendConfirmationEmail(email);
      if (error) {
        throw new Error(error);
      }

      toast({
        title: "Correo reenviado",
        description:
          "Se ha enviado un nuevo correo de confirmación a tu dirección de email",
      });
    } catch (error) {
      console.error("Error resending confirmation email:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "No se pudo reenviar el correo de confirmación",
        variant: "destructive",
      });
    } finally {
      setResendingEmail(false);
    }
  };

  // Detectar si el usuario fue redirigido porque su sesión requiere 2FA
  useEffect(() => {
    if (searchParams.get("mfa_required") === "true") {
      supabase.auth.mfa.getAuthenticatorAssuranceLevel().then(async ({ data }) => {
        if (data && data.currentLevel === "aal1" && data.nextLevel === "aal2") {
          const { data: factors } = await supabase.auth.mfa.listFactors();
          const totpFactor = factors?.totp?.find(f => f.status === "verified");
          if (totpFactor) {
            setMfaFactorId(totpFactor.id);
            setMfaRequired(true);
          }
        }
      });
    }
  }, [searchParams]);

  const proceedWithRedirect = async () => {
    // Get the session after sign in to pass it in the URL hash
    const { data: { session } } = await supabase.auth.getSession();

    const returnUrl = searchParams.get("returnUrl") || 
                      sessionStorage.getItem("post_session_expired_redirect") || 
                      localStorage.getItem("post_session_expired_redirect") || 
                      "/dashboard";

    // Clear the stored redirect after retrieval
    sessionStorage.removeItem("post_session_expired_redirect");
    localStorage.removeItem("post_session_expired_redirect");

    const currentHost = window.location.hostname;
    
    // Skip redirect in local development to avoid breaking the local dev environment
    const isLocalhost = currentHost === "localhost" || currentHost === "127.0.0.1" || currentHost.startsWith("192.168.");
    
    if (isLocalhost || currentHost.startsWith("app.")) {
      navigate(returnUrl);
    } else {
      const protocol = window.location.protocol;
      const port = window.location.port ? `:${window.location.port}` : "";
      
      let newHost = currentHost;
      if (currentHost.startsWith("www.")) {
        newHost = currentHost.replace("www.", "app.");
      } else {
        newHost = `app.${currentHost}`;
      }
      
      let hash = "";
      if (session) {
        hash = `#access_token=${session.access_token}&refresh_token=${session.refresh_token}&expires_in=${session.expires_in}&token_type=bearer&type=recovery`;
      }
      
      window.location.href = `${protocol}//${newHost}${port}${returnUrl}${hash}`;
    }
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      
      // Comprobar si se requiere un segundo factor (2FA / MFA)
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalData && aalData.currentLevel === "aal1" && aalData.nextLevel === "aal2") {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totpFactor = factors?.totp?.find(f => f.status === "verified");
        if (totpFactor) {
          setMfaFactorId(totpFactor.id);
          setMfaRequired(true);
          setLoading(false);
          return;
        }
      }

      await proceedWithRedirect();
    } catch (error) {
      console.error("Error logging in:", error);
      let errorMessage =
        "No se pudo iniciar sesión. Verifique sus credenciales.";
      let fieldError = null;

      if (error instanceof AuthApiError) {
        // Handle Supabase AuthApiError specifically

        if (error.message.includes("Email not confirmed")) {
          errorMessage =
            "No existe una cuenta con este correo electrónico o el correo electrónico no ha sido confirmado.";
          fieldError = "email";
        } else if (error.message.includes("Invalid login credentials")) {
          // For "Invalid login credentials", we need to determine if it's user not found or wrong password
          // We can check the error status or try to determine from the context
          if (error.status === 400) {
            // This could be either user not found or wrong password
            // For security, we'll show a generic message but we can try to determine if user exists
            errorMessage = "Correo electrónico o contraseña incorrectos.";

            // Set a general form error to trigger the helpful suggestions display
            form.setError("root", {
              type: "manual",
              message: "Error de credenciales",
            });
          }
        } else if (error.message.includes("Too many requests")) {
          errorMessage = "Demasiados intentos. Por favor, intente más tarde.";
        } else if (error.message.includes("User not found")) {
          errorMessage = "No existe una cuenta con este correo electrónico.";
          fieldError = "email";
        } else if (error.message.includes("Invalid email")) {
          errorMessage = "El formato del correo electrónico no es válido.";
          fieldError = "email";
        } else if (error.message.includes("Invalid password")) {
          errorMessage = "La contraseña es incorrecta.";
          fieldError = "password";
        }

        // Log the full error for debugging
      } else if (error instanceof AuthError) {
        // Handle other AuthError types
        if (error.message.includes("Email not confirmed")) {
          errorMessage =
            "No existe una cuenta con este correo electrónico o el correo electrónico no ha sido confirmado.";
          fieldError = "email";
        } else if (error.message.includes("Invalid login credentials")) {
          errorMessage = "Correo electrónico o contraseña incorrectos.";
        } else if (error.message.includes("Too many requests")) {
          errorMessage = "Demasiados intentos. Por favor, intente más tarde.";
        } else if (error.message.includes("User not found")) {
          errorMessage = "No existe una cuenta con este correo electrónico.";
          fieldError = "email";
        } else if (error.message.includes("Invalid email")) {
          errorMessage = "El formato del correo electrónico no es válido.";
          fieldError = "email";
        } else if (error.message.includes("Invalid password")) {
          errorMessage = "La contraseña es incorrecta.";
          fieldError = "password";
        }
      } else if (error instanceof Error) {
        // Handle other types of errors
        if (
          error.message.includes("user not found") ||
          error.message.includes("no user")
        ) {
          errorMessage = "No existe una cuenta con este correo electrónico.";
          fieldError = "email";
        } else if (
          error.message.includes("password") ||
          error.message.includes("contraseña")
        ) {
          errorMessage = "La contraseña es incorrecta.";
          fieldError = "password";
        } else {
          errorMessage = error.message || "Error inesperado al iniciar sesión.";
        }
      }

      // Set field-specific errors if available
      if (fieldError === "email") {
        form.setError("email", {
          type: "manual",
          message: errorMessage,
        });
      } else if (fieldError === "password") {
        form.setError("password", {
          type: "manual",
          message: errorMessage,
        });
      }

      // Show toast with error message
      toast({
        title: "Error de inicio de sesión",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async (codeToVerify?: string) => {
    const code = (codeToVerify || mfaCode).trim();
    if (!mfaFactorId || code.length < 6) return;
    setMfaLoading(true);
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: mfaFactorId,
        code,
      });
      if (error) throw error;
      await proceedWithRedirect();
    } catch (err: any) {
      toast({
        title: "Error 2FA",
        description: "Código incorrecto o expirado. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleCancelMfa = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn("Error signing out on MFA cancel:", e);
    }
    setMfaRequired(false);
    setMfaFactorId(null);
    setMfaCode("");
  };

  const handleMfaCodeInput = (val: string) => {
    const cleaned = val.replace(/\D/g, "").slice(0, 6);
    setMfaCode(cleaned);
    if (cleaned.length === 6 && mfaFactorId && !mfaLoading) {
      handleVerifyMfa(cleaned);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Section - Features & Capabilities */}
        <div className="hidden lg:flex lg:w-3/5 bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-50/60 p-12 xl:p-16 flex-col justify-between relative overflow-hidden border-r border-slate-200/80">
          {/* Ambient lighting */}
          <div className="absolute top-10 right-10 w-96 h-96 bg-blue-300/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-indigo-200/30 rounded-full blur-3xl pointer-events-none" />

          {/* Top Logo */}
          <div className="relative z-10">
            <Link to="/" className="inline-block hover:opacity-90 transition-opacity">
              <Logo className="text-xl" />
            </Link>
          </div>

          {/* Center Content */}
          <div className="max-w-xl my-auto relative z-10 py-8">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100/80 border border-blue-200 text-blue-800 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-blue-600" />
              <span>Sistema Operativo Legal con IA</span>
            </div>

            <h2 className="text-4xl xl:text-5xl font-black text-slate-900 mb-4 leading-tight tracking-tight">
              Bienvenido de <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">vuelta</span>
            </h2>

            <p className="text-slate-600 text-base xl:text-lg mb-8 leading-relaxed font-normal">
              Continúa gestionando tus expedientes, liquidaciones judiciales y consultas con respaldo en más de 1.8M de providencias de Altas Cortes.
            </p>

            {/* Platform Highlights */}
            <div className="space-y-3.5">
              <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-white/90 border border-slate-200/90 shadow-sm backdrop-blur-sm hover:border-blue-200 transition-all">
                <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0 text-blue-600">
                  <Scale className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-900">Jurisprudencia de Altas Cortes en Vivo</h4>
                  <p className="text-xs text-slate-500">Citas vinculantes oficiales y cero alucinaciones normativas.</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-white/90 border border-slate-200/90 shadow-sm backdrop-blur-sm hover:border-blue-200 transition-all">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0 text-indigo-600">
                  <FolderOpen className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-900">Control de Expedientes y Términos</h4>
                  <p className="text-xs text-slate-500">Monitoreo automático de radicados en despachos de la Rama Judicial.</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5 p-4 rounded-2xl bg-white/90 border border-slate-200/90 shadow-sm backdrop-blur-sm hover:border-blue-200 transition-all">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0 text-emerald-600">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-900">Cifrado Bancario y Habeas Data</h4>
                  <p className="text-xs text-slate-500">Máxima confidencialidad y protección bajo la Ley 1581 de 2012.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="relative z-10 pt-4 border-t border-slate-200/80 flex items-center justify-between text-xs text-slate-500">
            <span>© {new Date().getFullYear()} MíA • Asistente Legal IA en Colombia</span>
            <Link to="/#faq" className="hover:text-blue-600 transition-colors">
              Ayuda & Preguntas Frecuentes
            </Link>
          </div>
        </div>

        {/* Right Section - Login Form */}
        <div className="w-full lg:w-2/5 bg-white dark:bg-slate-900 flex items-center justify-center p-6 sm:p-12 border-l border-slate-200/80 dark:border-slate-800">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden mb-8">
              <Link to="/">
                <Logo />
              </Link>
            </div>

            <div className="mb-8">
              <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
                Iniciar Sesión
              </h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                Ingresa con tus credenciales seguras para acceder a tu despacho
              </p>
            </div>

            {mfaRequired ? (
              <div className="space-y-6 animate-fade-in">
                <div className="text-center space-y-2">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center mx-auto text-blue-600">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Verificación en Dos Pasos (2FA)</h3>
                  <p className="text-xs text-slate-500 max-w-sm mx-auto">
                    Ingresa el código de 6 dígitos generado por tu aplicación de autenticación (Google Authenticator, Authy o Apple Passwords).
                  </p>
                </div>

                <div className="space-y-4">
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => handleMfaCodeInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && mfaCode.trim().length === 6) {
                        handleVerifyMfa();
                      }
                    }}
                    placeholder="123456"
                    className="text-center text-2xl tracking-widest font-mono h-14 rounded-xl border-slate-300"
                    autoFocus
                  />

                  <Button
                    type="button"
                    className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl shadow-md cursor-pointer"
                    onClick={() => handleVerifyMfa()}
                    disabled={mfaLoading || mfaCode.trim().length < 6}
                  >
                    {mfaLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verificando...
                      </>
                    ) : (
                      "Verificar y Entrar"
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-xs text-slate-500 hover:text-slate-800 cursor-pointer"
                    onClick={handleCancelMfa}
                  >
                    Volver a ingresar credenciales
                  </Button>

                  <p className="text-xs text-center text-slate-400 pt-2 leading-relaxed">
                    ¿Perdiste tu dispositivo de autenticación?{" "}
                    <a 
                      href="mailto:soporte@miasistentelegalia.com?subject=Recuperaci%C3%B3n%20de%20Cuenta%202FA"
                      className="text-blue-600 underline font-medium hover:text-blue-700"
                    >
                      Contactar a Soporte
                    </a>
                  </p>
                </div>
              </div>
            ) : (
              <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6">
                {/* General Error Display */}
                {form.formState.errors.root && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">
                      {form.formState.errors.root.message}
                    </p>
                  </div>
                )}

                {/* Email Confirmation Error Display */}
                {form.formState.errors.email &&
                  form.formState.errors.email.message?.includes(
                    "verifique su correo electrónico"
                  ) && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-amber-400"
                            viewBox="0 0 20 20"
                            fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-amber-800">
                            Correo electrónico no confirmado
                          </h3>
                          <p className="mt-1 text-sm text-amber-700">
                            {form.formState.errors.email.message}
                          </p>
                          <div className="mt-3">
                            <p className="text-sm text-amber-700 mb-2">
                              ¿No recibiste el correo de confirmación?
                            </p>
                            <button
                              type="button"
                              onClick={handleResendConfirmationEmail}
                              disabled={resendingEmail}
                              className="text-sm text-amber-600 hover:text-amber-500 font-medium underline disabled:opacity-50 disabled:cursor-not-allowed">
                              {resendingEmail
                                ? "Reenviando..."
                                : "Reenviar correo de confirmación"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Correo Electrónico
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="abogado@tudespacho.com"
                          className={`h-12 bg-slate-50/70 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl focus:bg-white dark:focus:bg-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-slate-900 dark:text-white placeholder:text-slate-400 ${form.formState.errors.email ||
                            form.formState.errors.root
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                            : ""
                            }`}
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            clearFieldError("email");
                            if (form.formState.errors.root) {
                              form.clearErrors("root");
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                          Contraseña
                        </FormLabel>
                        <Link
                          to="/forgot-password"
                          className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 hover:underline">
                          ¿Olvidaste tu contraseña?
                        </Link>
                      </div>
                      <div className="relative">
                        <FormControl>
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••••••"
                            className={`h-12 bg-slate-50/70 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-xl pr-12 focus:bg-white dark:focus:bg-slate-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-slate-900 dark:text-white placeholder:text-slate-400 ${form.formState.errors.password ||
                              form.formState.errors.root
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                              : ""
                              }`}
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              clearFieldError("password");
                              if (form.formState.errors.root) {
                                form.clearErrors("root");
                              }
                            }}
                          />
                        </FormControl>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={
                            showPassword
                              ? "Ocultar contraseña"
                              : "Mostrar contraseña"
                          }
                          className="absolute right-1 top-1 h-10 w-10 p-0 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                          onClick={() => setShowPassword((value) => !value)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* General helpful suggestions for any login error */}
                {(form.formState.errors.email ||
                  form.formState.errors.password ||
                  form.formState.errors.root) && (
                    <div className="p-4 bg-blue-50/80 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/60 rounded-xl">
                      <p className="text-xs font-bold text-blue-900 dark:text-blue-300 mb-2 uppercase tracking-wider">
                        ¿Necesitas ayuda para iniciar sesión?
                      </p>
                      <div className="space-y-1.5 text-xs text-blue-700 dark:text-blue-300">
                        <Link
                          to="/forgot-password"
                          className="block font-medium hover:underline">
                          • Restablecer contraseña olvidada
                        </Link>
                        <Link
                          to="/signup"
                          className="block font-medium hover:underline">
                          • ¿No tienes una cuenta aún? Crea tu despacho gratis
                        </Link>
                      </div>
                    </div>
                  )}

                <div className="flex justify-center my-3">
                  <Turnstile onVerify={() => {}} />
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold text-base rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/35 transition-all flex items-center justify-center gap-2 group cursor-pointer"
                    disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Iniciando sesión...
                      </>
                    ) : (
                      <>
                        <span>Ingresar a mi Despacho</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-4 leading-relaxed">
                  Al iniciar sesión o registrarte, aceptas nuestros{" "}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-blue-600 dark:text-blue-400 underline font-semibold hover:text-blue-700">
                    Términos de Servicio y Protección de Datos
                  </button>
                  .
                </p>
              </form>
            </Form>
            )}

            <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800 text-center">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                ¿Aún no tienes una cuenta?{" "}
                <Link
                  to="/signup"
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 font-bold hover:underline">
                  Crear cuenta gratis (3 días)
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </div>
  );
}

